import type { AudioEngine } from './engine';

/**
 * One-shot sound effects, all synthesized from oscillators and filtered
 * bursts of a shared white-noise buffer — no sample files. Every method
 * schedules at context.currentTime, routes to the sfx bus, and silently
 * no-ops until the engine is unlocked.
 */

/** A4-rooted major pentatonic (semitone offsets), shared vocabulary with chirps. */
const PENTATONIC = [0, 2, 4, 7, 9] as const;

function pentatonicHz(degree: 0 | 1 | 2 | 3 | 4, octave: number): number {
  return 440 * Math.pow(2, (PENTATONIC[degree] + octave * 12) / 12);
}

export class Sfx {
  private readonly engine: AudioEngine;
  private noiseBuffer: AudioBuffer | null = null;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  private get ready(): boolean {
    return this.engine.context.state === 'running';
  }

  /** Shared 1 s white-noise buffer, created lazily once. */
  private noise(): AudioBuffer {
    const ctx = this.engine.context;
    if (!this.noiseBuffer) {
      const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
    }
    return this.noiseBuffer;
  }

  /** Gain envelope (linear attack, exponential decay) routed to the sfx bus. */
  private env(t0: number, peak: number, attack: number, decay: number): GainNode {
    const g = this.engine.context.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
    g.connect(this.engine.sfxBus);
    return g;
  }

  /** Noise source playing [t0, t0+duration), starting at a varied buffer offset. */
  private noiseSource(t0: number, duration: number): AudioBufferSourceNode {
    const src = this.engine.context.createBufferSource();
    src.buffer = this.noise();
    src.loop = true;
    src.start(t0, Math.random() * 0.5, duration);
    return src;
  }

  /** Warm boot-tap blip: sine sweeping 200 -> 800 Hz over 120 ms. */
  powerOn(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, t0);
    osc.frequency.exponentialRampToValueAtTime(800, t0 + 0.12);
    osc.connect(this.env(t0, 0.25, 0.01, 0.11));
    osc.start(t0);
    osc.stop(t0 + 0.14);
  }

  /** Short filtered 'crack'; step 2 is slightly bigger and lower. */
  eggCrack(step: 1 | 2): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const big = step === 2;
    const src = this.noiseSource(t0, big ? 0.08 : 0.05);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = big ? 1400 : 2200;
    filter.Q.value = 1.2;
    src.connect(filter);
    filter.connect(this.env(t0, big ? 0.35 : 0.28, 0.002, big ? 0.07 : 0.045));
  }

  /** Bright hatch: noise splash plus a rising major-pentatonic 3-note flourish. */
  hatch(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;

    const splash = this.noiseSource(t0, 0.2);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(3200, t0);
    filter.frequency.exponentialRampToValueAtTime(1400, t0 + 0.18);
    filter.Q.value = 0.9;
    splash.connect(filter);
    filter.connect(this.env(t0, 0.22, 0.004, 0.18));

    // A5, C#6, E6 — rising and squarely inside the creatures' pentatonic world.
    const notes = [pentatonicHz(0, 1), pentatonicHz(2, 1), pentatonicHz(3, 1)];
    notes.forEach((freq, i) => {
      const t = t0 + 0.05 + i * 0.06;
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freq * (1 + (Math.random() - 0.5) * 0.004);
      osc.connect(this.env(t, 0.2, 0.006, 0.13));
      osc.start(t);
      osc.stop(t + 0.15);
    });
  }

  /** Bandpassed noise sweeping down, 200 ms (apple flying). */
  whoosh(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const src = this.noiseSource(t0, 0.2);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2400, t0);
    filter.frequency.exponentialRampToValueAtTime(350, t0 + 0.2);
    filter.Q.value = 1.5;
    src.connect(filter);
    filter.connect(this.env(t0, 0.2, 0.03, 0.17));
  }

  /** Hollow woodblock knock (apple lands): fast sine drop plus a click. */
  thock(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(320, t0);
    osc.frequency.exponentialRampToValueAtTime(180, t0 + 0.04);
    osc.connect(this.env(t0, 0.35, 0.002, 0.09));
    osc.start(t0);
    osc.stop(t0 + 0.11);

    const click = this.noiseSource(t0, 0.012);
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 2500;
    click.connect(filter);
    filter.connect(this.env(t0, 0.12, 0.001, 0.011));
  }

  /** Three quick lowpassed noise bursts, descending (eating). */
  crunch(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const cutoffs = [2200, 1500, 1000];
    cutoffs.forEach((cutoff, i) => {
      const t = t0 + i * 0.075;
      const src = this.noiseSource(t, 0.05);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = cutoff;
      filter.Q.value = 0.8;
      src.connect(filter);
      filter.connect(this.env(t, 0.28 - i * 0.05, 0.003, 0.045));
    });
  }

  /** Single rubber squeak; pitch 600-1400 Hz scaled by speed 0..1. */
  squeak(speed: number): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const s = Math.min(1, Math.max(0, speed));
    const peak = (600 + 800 * s) * (1 + (Math.random() - 0.5) * 0.04);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    // pitch scoops up into the squeak then sags — wet rubber
    osc.frequency.setValueAtTime(peak * 0.7, t0);
    osc.frequency.exponentialRampToValueAtTime(peak, t0 + 0.045);
    osc.frequency.exponentialRampToValueAtTime(peak * 0.85, t0 + 0.09);
    osc.connect(this.env(t0, 0.14 + 0.08 * s, 0.012, 0.08));
    osc.start(t0);
    osc.stop(t0 + 0.11);
  }

  /** Bubble pop plus a tiny high chime (grime cleaned). */
  sparklePop(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;

    const pop = ctx.createOscillator();
    pop.type = 'sine';
    pop.frequency.setValueAtTime(600, t0);
    pop.frequency.exponentialRampToValueAtTime(150, t0 + 0.04);
    pop.connect(this.env(t0, 0.25, 0.002, 0.05));
    pop.start(t0);
    pop.stop(t0 + 0.06);

    // A6 chime, delayed a hair so the pop reads first
    const chime = ctx.createOscillator();
    chime.type = 'sine';
    chime.frequency.value = pentatonicHz(0, 2) * (1 + (Math.random() - 0.5) * 0.004);
    chime.connect(this.env(t0 + 0.03, 0.12, 0.005, 0.2));
    chime.start(t0 + 0.03);
    chime.stop(t0 + 0.25);
  }

  /** Comedic low blorp: wobbling sine 180 -> 70 Hz, 150 ms (grime appears). */
  plop(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, t0);
    osc.frequency.exponentialRampToValueAtTime(70, t0 + 0.15);

    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 16;
    const wobble = ctx.createGain();
    wobble.gain.value = 14;
    lfo.connect(wobble);
    wobble.connect(osc.frequency);

    osc.connect(this.env(t0, 0.35, 0.008, 0.14));
    osc.start(t0);
    lfo.start(t0);
    osc.stop(t0 + 0.16);
    lfo.stop(t0 + 0.16);
  }

  /** Springy bounce; intensity 0..1 scales start pitch and level (ball). */
  boing(intensity: number): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const k = Math.min(1, Math.max(0, intensity));
    const start = 240 + 420 * k;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(start, t0);
    osc.frequency.exponentialRampToValueAtTime(start * 0.35, t0 + 0.22);

    // decaying spring wobble on top of the fall
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 22;
    const wobble = ctx.createGain();
    wobble.gain.setValueAtTime(start * 0.12, t0);
    wobble.gain.exponentialRampToValueAtTime(0.5, t0 + 0.22);
    lfo.connect(wobble);
    wobble.connect(osc.frequency);

    osc.connect(this.env(t0, 0.12 + 0.16 * k, 0.006, 0.21));
    osc.start(t0);
    lfo.start(t0);
    osc.stop(t0 + 0.24);
    lfo.stop(t0 + 0.24);
  }

  /** Tiny click for tray buttons — roughly one cycle of sine. */
  uiTap(): void {
    if (!this.ready) return;
    const ctx = this.engine.context;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 1000;
    osc.connect(this.env(t0, 0.15, 0.001, 0.03));
    osc.start(t0);
    osc.stop(t0 + 0.035);
  }
}
